import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ICareHome } from '../../interfaces';
import type { IAccount } from '../../interfaces/Account/IAccount.ts';
import type { IUpdateAccountPayload } from '../../interfaces/Account/IUpdateAccountPayload.ts';
import AccountService from '../../services/AccountService.ts';
import CareHomeService from '../../services/CareHomeService.ts';

export function useAppAccountsPageMutations() {
  const queryClient = useQueryClient();

  const invalidateAppAccounts = async () => {
    await queryClient.invalidateQueries({ queryKey: ['appAccounts'] });
  };

  const invalidateActiveCount = async () => {
    await queryClient.invalidateQueries({ queryKey: ['activeAppAccountsCount'] });
  };

  const refreshCareHomeRegistrationIdMutation = useMutation({
    mutationFn: (variables: { careHomeId: string; registrationId: string }) =>
      CareHomeService.updateCareHomeRegistrationId(variables.careHomeId, variables.registrationId),
    onSuccess: (updated) => {
      queryClient.setQueryData<ICareHome>(['careHome', updated.id], updated);
    },
  });

  const updateCaregiverActiveStatusMutation = useMutation({
    mutationFn: (variables: { caregiver: IAccount; isActive: boolean }) => {
      const caregiver = variables.caregiver;
      const payload: IUpdateAccountPayload = {
        organisation_id: caregiver.organisation_id ?? null,
        carehome_id: caregiver.carehome_id || undefined,
        first_name: caregiver.first_name,
        last_name: caregiver.last_name,
        email: caregiver.email,
        username: caregiver.username,
        role: caregiver.role,
        is_active: variables.isActive,
        lang: caregiver.lang,
        title: caregiver.title || undefined,
      };
      return AccountService.updateAccount(caregiver.id, payload);
    },
    onSuccess: async () => {
      await invalidateAppAccounts();
      await invalidateActiveCount();
    },
  });

  return {
    refreshCareHomeRegistrationIdMutation,
    updateCaregiverActiveStatusMutation,
    invalidateAppAccounts,
    invalidateActiveCount,
    isCrudLoading:
      refreshCareHomeRegistrationIdMutation.isPending ||
      updateCaregiverActiveStatusMutation.isPending,
  };
}

